import React, { useEffect, useState } from "react";
import { Autocomplete, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { Link as LinkIcon, PlaceOutlined } from "@mui/icons-material";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";
import { getSafeLink } from "./reelEdit";

const MAX_HASHTAGS = 30;
const MAX_TAGGED_USERS = 20;

const TEXT = {
  es: {
    hashtags: "Hashtags",
    hashtagPlaceholder: "Escribe un hashtag",
    add: "Agregar",
    tagPeople: "Etiquetar personas",
    searchPeople: "Buscar personas...",
    location: "Ubicación",
    locationPlaceholder: "Ciudad o lugar",
    link: "Enlace",
    linkPlaceholder: "https://...",
    invalidLink: "Ese enlace no es válido.",
    audience: "Público",
    audiencePublic: "Público",
    audienceFollowers: "Seguidores",
    audienceCloseFriends: "Mejores amigos",
    closeFriendsPlaceholder: "Elige entre tus seguidores...",
    noCloseFriends: "Elige al menos un mejor amigo.",
  },
  en: {
    hashtags: "Hashtags",
    hashtagPlaceholder: "Type a hashtag",
    add: "Add",
    tagPeople: "Tag people",
    searchPeople: "Search people...",
    location: "Location",
    locationPlaceholder: "City or place",
    link: "Link",
    linkPlaceholder: "https://...",
    invalidLink: "That link is not valid.",
    audience: "Audience",
    audiencePublic: "Public",
    audienceFollowers: "Followers",
    audienceCloseFriends: "Close friends",
    closeFriendsPlaceholder: "Choose from your followers...",
    noCloseFriends: "Choose at least one close friend.",
  },
};

const AUDIENCES = [
  { id: "public", labelKey: "audiencePublic" },
  { id: "followers", labelKey: "audienceFollowers" },
  { id: "close_friends", labelKey: "audienceCloseFriends" },
];

const personLabel = (person) => person.displayName || person.username || "";

export const usePostDetails = ({ user, people = [], language = "es" }) => {
  const t = useTranslatedDictionary(TEXT, language);
  const [hashtags, setHashtags] = useState([]);
  const [draftHashtag, setDraftHashtag] = useState("");
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [location, setLocation] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [audience, setAudience] = useState("public");
  const [closeFriendUids, setCloseFriendUids] = useState([]);

  const uid = user?.uid;
  const followers = people.filter((person) => person.following?.includes(uid));
  const audienceUids = audience === "close_friends"
    ? followers.filter((person) => closeFriendUids.includes(person.uid)).map((person) => person.uid)
    : [];

  useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, "users", uid, "private", "data"))
      .then((snap) => {
        if (snap.exists() && Array.isArray(snap.data().closeFriends)) setCloseFriendUids(snap.data().closeFriends);
      })
      .catch(() => {});
  }, [uid]);

  const addHashtag = () => {
    const tag = (draftHashtag.match(/[\p{L}\p{N}_]+/gu) || []).join("");
    if (tag && !hashtags.includes(tag) && hashtags.length < MAX_HASHTAGS) setHashtags((prev) => [...prev, tag]);
    setDraftHashtag("");
  };

  return {
    t,
    hashtags,
    setHashtags,
    draftHashtag,
    setDraftHashtag,
    addHashtag,
    taggedUsers,
    setTaggedUsers,
    location,
    setLocation,
    linkUrl,
    setLinkUrl,
    audience,
    setAudience,
    closeFriendUids,
    setCloseFriendUids,
    followers,
    people,
    // Devuelve el mensaje de error, o "" si se puede publicar.
    validate: () => {
      if (linkUrl.trim() && !getSafeLink(linkUrl)) return t("invalidLink");
      if (audience === "close_friends" && audienceUids.length === 0) return t("noCloseFriends");
      return "";
    },
    // Los hashtags van en el texto: de ahí los leen el buscador y el orden del feed.
    withHashtags: (text) => [text.trim(), hashtags.map((tag) => `#${tag}`).join(" ")].filter(Boolean).join("\n"),
    saveCloseFriends: async () => {
      if (audience !== "close_friends") return;
      await setDoc(doc(db, "users", uid, "private", "data"), { closeFriends: audienceUids }, { merge: true });
    },
    fields: () => ({
      location: location.trim(),
      linkUrl: getSafeLink(linkUrl),
      taggedUsers: taggedUsers.map((person) => ({
        uid: person.uid,
        username: person.username || "",
        displayName: person.displayName || "",
      })),
      audience,
      audienceUids,
    }),
    reset: () => {
      setHashtags([]);
      setDraftHashtag("");
      setTaggedUsers([]);
      setLocation("");
      setLinkUrl("");
      setAudience("public");
    },
  };
};

export const PostDetailsFields = ({ details, disabled = false, isLightTheme = false }) => {
  const { t } = details;
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const labelSx = { display: "block", mt: 1.25, mb: 0.25, fontWeight: 700, color: onSurface(1) };
  const inputProps = { disableUnderline: true, sx: { color: onSurface(1) } };
  const autocompleteSlotProps = {
    paper: {
      sx: {
        bgcolor: isLightTheme ? "#fff" : "#141414",
        color: onSurface(1),
        border: `1px solid ${onSurface(0.1)}`,
      },
    },
  };
  const autocompleteSx = {
    "& .MuiChip-root": { color: onSurface(1), bgcolor: onSurface(0.12) },
    "& .MuiChip-deleteIcon": { color: onSurface(0.7) },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { color: onSurface(0.7) },
  };
  const renderPeopleInput = (placeholder) => (params) => (
    <TextField
      {...params}
      variant="standard"
      placeholder={placeholder}
      InputProps={{ ...params.InputProps, ...inputProps }}
    />
  );

  return (
    <>
      <Typography variant="caption" sx={labelSx}>{t("hashtags")}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          size="small"
          variant="standard"
          placeholder={t("hashtagPlaceholder")}
          value={details.draftHashtag}
          disabled={disabled}
          onChange={(e) => details.setDraftHashtag(e.target.value.slice(0, 50))}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            details.addHashtag();
          }}
          InputProps={inputProps}
        />
        <Button
          onClick={details.addHashtag}
          disabled={disabled || !details.draftHashtag.trim()}
          sx={{ color: "#00d4ff", textTransform: "none", fontWeight: 700 }}
        >
          {t("add")}
        </Button>
      </Stack>
      {details.hashtags.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
          {details.hashtags.map((tag) => (
            <Chip
              key={tag}
              size="small"
              label={`#${tag}`}
              onDelete={disabled ? undefined : () => details.setHashtags((prev) => prev.filter((item) => item !== tag))}
              sx={{ color: "#00b8d9", bgcolor: "rgba(0,212,255,0.12)", "& .MuiChip-deleteIcon": { color: onSurface(0.7) } }}
            />
          ))}
        </Stack>
      )}

      <Typography variant="caption" sx={labelSx}>{t("tagPeople")}</Typography>
      <Autocomplete
        multiple
        size="small"
        disabled={disabled}
        options={details.people}
        value={details.taggedUsers}
        onChange={(_, value) => details.setTaggedUsers(value.slice(0, MAX_TAGGED_USERS))}
        getOptionLabel={personLabel}
        isOptionEqualToValue={(option, value) => option.uid === value.uid}
        slotProps={autocompleteSlotProps}
        sx={autocompleteSx}
        renderInput={renderPeopleInput(t("searchPeople"))}
      />

      <Typography variant="caption" sx={labelSx}>{t("location")}</Typography>
      <TextField
        fullWidth
        size="small"
        variant="standard"
        placeholder={t("locationPlaceholder")}
        value={details.location}
        disabled={disabled}
        onChange={(e) => details.setLocation(e.target.value.slice(0, 80))}
        InputProps={inputProps}
      />

      <Typography variant="caption" sx={labelSx}>{t("link")}</Typography>
      <TextField
        fullWidth
        size="small"
        variant="standard"
        placeholder={t("linkPlaceholder")}
        value={details.linkUrl}
        disabled={disabled}
        onChange={(e) => details.setLinkUrl(e.target.value.slice(0, 500))}
        InputProps={inputProps}
      />

      <Typography variant="caption" sx={labelSx}>{t("audience")}</Typography>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1, mb: 1 }}>
        {AUDIENCES.map((option) => {
          const selected = details.audience === option.id;
          return (
            <Chip
              key={option.id}
              label={t(option.labelKey)}
              disabled={disabled}
              onClick={() => details.setAudience(option.id)}
              sx={{
                fontWeight: 600,
                color: selected ? "#041015" : onSurface(1),
                bgcolor: selected ? "#00d4ff" : onSurface(0.1),
                "&:hover": { bgcolor: selected ? "#00d4ff" : onSurface(0.18) },
              }}
            />
          );
        })}
      </Stack>
      {details.audience === "close_friends" && (
        <Autocomplete
          multiple
          size="small"
          disabled={disabled}
          options={details.followers}
          value={details.followers.filter((person) => details.closeFriendUids.includes(person.uid))}
          onChange={(_, value) => details.setCloseFriendUids(value.map((person) => person.uid))}
          getOptionLabel={personLabel}
          isOptionEqualToValue={(option, value) => option.uid === value.uid}
          slotProps={autocompleteSlotProps}
          sx={{ ...autocompleteSx, mb: 1 }}
          renderInput={renderPeopleInput(t("closeFriendsPlaceholder"))}
        />
      )}
    </>
  );
};

// Muestra los detalles de una publicación. El contenido lo escribió otro usuario.
export const PostDetailsInfo = ({ post, onOpenProfile, isLightTheme = false, language = "es" }) => {
  const t = useTranslatedDictionary(TEXT, language);
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const safeLink = getSafeLink(post?.linkUrl);
  const taggedUsers = Array.isArray(post?.taggedUsers) ? post.taggedUsers.filter((person) => person?.uid) : [];
  const audienceLabel = { followers: t("audienceFollowers"), close_friends: t("audienceCloseFriends") }[post?.audience];

  if (!post?.location && !safeLink && taggedUsers.length === 0 && !audienceLabel) return null;

  return (
    <Stack spacing={0.4} sx={{ mt: 0.75 }}>
      {audienceLabel && (
        <Typography variant="caption" sx={{ color: "#00b8d9", fontWeight: 700 }}>{audienceLabel}</Typography>
      )}
      {post.location && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: onSurface(0.75) }}>
          <PlaceOutlined sx={{ fontSize: 15 }} />
          <Typography variant="caption" noWrap>{String(post.location).slice(0, 80)}</Typography>
        </Stack>
      )}
      {taggedUsers.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 1 }}>
          {taggedUsers.slice(0, MAX_TAGGED_USERS).map((person) => (
            <Typography
              key={person.uid}
              variant="caption"
              onClick={onOpenProfile ? () => onOpenProfile(person.uid) : undefined}
              sx={{ color: "#00b8d9", fontWeight: 700, cursor: onOpenProfile ? "pointer" : "default" }}
            >
              @{person.username || person.displayName}
            </Typography>
          ))}
        </Stack>
      )}
      {safeLink && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={() => window.open(safeLink, "_blank", "noopener,noreferrer")}
          sx={{ width: "fit-content", maxWidth: "100%", cursor: "pointer", color: "#00b8d9" }}
        >
          <LinkIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>{new URL(safeLink).hostname}</Typography>
        </Stack>
      )}
    </Stack>
  );
};
